import { ref, reactive, computed, watch, onBeforeUnmount } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { pinia } from '@/stores';
import { useAppointmentStore } from '@/stores/AppointmentStore';
import { buildRouteProblems } from '@/utils/legacy/agendaRouteSource';
import { applyOptimization } from '@/utils/legacy/agendaWriter';
import { optimizeRoute } from '@/services/routeOptimizationService';
import { STEP_KIND } from '@/utils/domain/routeOptimizer';
import { log } from '@/utils/core/vueLogger.js';

const DECISION = { KEEP: 'keep', REMOVE: 'remove' };

/**
 * State of the "Itinéraire optimisé" view (#23500): options, computation, staleness, unplaceable choices, apply.
 * The proposal never touches the agenda before apply() (RG-04) and is never recomputed automatically (RG-20).
 *
 * Unplaceable visits (RG-21) start without a choice and block apply until the user picks one: "Supprimer"
 * keeps the proposal, "Garder" makes it stale because the visit then becomes a fixed visit the others must
 * be laid out around (no silent deletion, no overlap).
 *
 * @param {import('vue').Ref<{viewType: string, start: number, end: number}|null>} scope  period sent by the legacy menu
 */
export function useRouteOptimizer(scope) {
	const store = useAppointmentStore(pinia);
	const options = reactive({ keepStart: true, keepEnd: true });
	const loading = ref(false);
	const applying = ref(false);
	const error = ref(null);
	const result = ref(null);
	const points = ref([]);
	const stale = ref(false);
	const decisions = reactive({});
	const isOnline = ref(iframeDataBridge.isOnline());
	let requestId = 0;
	let computedWith = null;
	let plannedWith = null;

	const uidsWith = (decision) =>
		Object.keys(decisions)
			.filter((uid) => decisions[uid] === decision)
			.sort();
	// The proposal depends on the options, the pins and the unplaceable visits kept on their slot.
	const planSignature = () => JSON.stringify({ keepStart: options.keepStart, keepEnd: options.keepEnd, pins: [...store.pinnedUids()].sort() });
	const signature = () => JSON.stringify({ plan: planSignature(), kept: uidsWith(DECISION.KEEP) });

	async function run() {
		if (!scope.value) return;
		const current = ++requestId;
		// Keep/Remove choices only hold for the options and pins they were made with.
		if (plannedWith !== null && planSignature() !== plannedWith) Object.keys(decisions).forEach((uid) => delete decisions[uid]);
		// Captured before the first await: a change made while the matrix loads must leave the result stale.
		const requested = { plan: planSignature(), all: signature(), options: { keepStart: options.keepStart, keepEnd: options.keepEnd }, kept: new Set(uidsWith(DECISION.KEEP)) };
		loading.value = true;
		error.value = null;
		try {
			const problems = await buildRouteProblems(scope.value, store.pinnedUids());
			const optimized = await optimizeRoute(problems, requested.options, requested.kept);
			if (current !== requestId) return;
			points.value = problems.points;
			result.value = optimized;
			computedWith = requested.all;
			plannedWith = requested.plan;
			stale.value = signature() !== computedWith;
			const listed = new Set(unplaceableOf(optimized).map((entry) => entry.item.uid));
			Object.keys(decisions).forEach((uid) => !listed.has(uid) && delete decisions[uid]);
		} catch (e) {
			if (current !== requestId) return;
			log('error', 'RouteOptimizer', `Optimization failed: ${e?.message || e}`);
			error.value = 'optimize';
		} finally {
			if (current === requestId) loading.value = false;
		}
	}

	function reset() {
		requestId++;
		result.value = null;
		points.value = [];
		error.value = null;
		stale.value = false;
		loading.value = false;
		applying.value = false;
		computedWith = null;
		plannedWith = null;
		options.keepStart = true;
		options.keepEnd = true;
		Object.keys(decisions).forEach((uid) => delete decisions[uid]);
	}

	watch([() => options.keepStart, () => options.keepEnd, () => store.pinned, () => uidsWith(DECISION.KEEP).join()], () => {
		if (computedWith) stale.value = signature() !== computedWith;
	});

	const unplaceable = computed(() => unplaceableOf(result.value));
	const undecided = computed(() => unplaceable.value.filter((entry) => !decisions[entry.item.uid]).length);
	const deletions = computed(() => unplaceable.value.filter((entry) => decisions[entry.item.uid] === DECISION.REMOVE).map((entry) => entry.item.uid));
	const hasWork = computed(() => !!result.value && (result.value.changes.length > 0 || deletions.value.length > 0));
	const canApply = computed(() => hasWork.value && !undecided.value && !stale.value && !loading.value && !applying.value && isOnline.value);

	async function apply() {
		if (!canApply.value) return false;
		applying.value = true;
		error.value = null;
		try {
			const { failed } = await applyOptimization({ changes: result.value.changes, deletions: deletions.value });
			if (failed.length) {
				log('warn', 'RouteOptimizer', `${failed.length} appointment(s) not saved: ${failed.join(' | ')}`);
				error.value = 'apply';
				return false;
			}
			return true;
		} catch (e) {
			log('error', 'RouteOptimizer', `Apply failed: ${e?.message || e}`);
			error.value = 'apply';
			return false;
		} finally {
			applying.value = false;
		}
	}

	const amplify = iframeDataBridge.getIframeWindow()?.amplify;
	const onOffline = () => (isOnline.value = iframeDataBridge.isOnline());
	amplify?.subscribe?.('evtOffline', onOffline);
	onBeforeUnmount(() => amplify?.unsubscribe?.('evtOffline', onOffline));

	return { options, loading, applying, error, result, points, stale, decisions, unplaceable, undecided, isOnline, canApply, hasWork, run, reset, apply, DECISION };
}

// Unplaceable visits of a result: left out of the proposal (no choice yet, or "Supprimer"), or kept on their slot.
function unplaceableOf(optimized) {
	return (optimized?.days || []).flatMap((day) => [...day.unplaceable.map((item) => ({ item, date: day.date })), ...day.steps.filter((step) => step.kind === STEP_KIND.UNPLACEABLE).map((step) => ({ item: step.item, date: day.date }))]);
}
